/**
 * WorldDbTableRow — Renders a single table row for a contact.
 * Memoized (React.memo) to avoid re-renders when row data hasn't changed.
 *
 * Handles all column-specific rendering: checkboxes, status badges, AI mail button,
 * spy button, WhatsApp, socials, decision makers, scores, intelligence bar, etc.
 *
 * @param {Object} props
 * @param {Object}   props.row - Contact data object
 * @param {string}   props.rowId - Firestore document ID
 * @param {number}   props.index - Row index in displayRows
 * @param {string[]} props.orderedColumns - Column keys to render (in order)
 * @param {boolean}  props.isSelectMode - Whether bulk selection is active
 * @param {Object}   props.selectedRows - Map of { rowId: boolean }
 * @param {Object}   props.editedData - Map of { "rowId-field": value } for pending edits
 * @param {Array}    props.warmupFlows - Active warmup flow definitions
 * @param {Object}   props.completingFlowStep - Map of { contactId: boolean } loading states
 * @param {Array}    props.emailTemplates - Available email templates for dropdown
 * @param {string}   props.userId - Current user ID
 * @param {Object}   props.spyLoadingRows - Map of { rowId: boolean } for spy loading state
 * @param {Function} props.onToggleRowSelection - (rowId, isShiftKey) => void
 * @param {Function} props.onEdit - (rowId, field, value) => void
 * @param {Function} props.onEmailAiClick - (index) => void
 * @param {Function} props.onSpyClick - (index) => void
 * @param {Function} props.onWhatsAppClick - (index) => void
 * @param {Function} props.onOpenFlowSelect - (rowId) => void
 * @param {Function} props.onOpenDecisionMakers - ({ isOpen, contactData, decisionMakers }) => void
 * @param {Function} props.onOpenSocialEdit - (rowId, row) => void
 * @param {Function} props.onOpenNotes - (row) => void
 * @param {Function} props.onOpenLeadStatus - (row) => void. Opens the lead-status panel.
 * @param {Function} props.onOpenIntel - (row) => void
 * @param {Function} props.onOpenIgResearch - (row) => void
 * @param {Function} props.onOpenSeoResearch - (row) => void
 * @param {Function} props.onFindSimilar - (row) => void
 *
 * Also exports: parseSocials(row) — Parses social links from row data (legacy + JSON)
 */
import React, { memo, useState } from "react";
import { createPortal } from "react-dom";
import { auth } from "../firebaseConfig";
import WarmupFlowBadge, { getFlowStepTheme } from './WarmupFlowBadge';
import { signalsAreFresh, enqueueForClassification } from './leadSignalEngine';
import { STRIKETHROUGH_TAGS, SOCIAL_PLATFORMS, PACING_OPTIONS, PACING_LABELS, getLeadScoreTier, getAllContactEmails } from '../shared/enums';
import { isAgentFailedStatus } from './outreachCompletedTag';
import { EMAIL_ACTION_TYPES } from '../shared/flowTouchpoints';

const socialPlatforms = SOCIAL_PLATFORMS;

function parseSocials(row) {
  let socials = {};
  try {
    if (row.lead_socials && typeof row.lead_socials === 'string') socials = JSON.parse(row.lead_socials);
    else if (row.lead_socials && typeof row.lead_socials === 'object') socials = row.lead_socials;
  } catch (e) { /* ignore */ }
  const fallbacks = {
    linkedin: row.lead_linkedin,
    instagram: row.lead_instagram || row.lead_instagram_username,
    facebook: row.lead_facebook,
    tiktok: row.lead_tiktok,
    x: row.lead_twitter,
    youtube: row.lead_youtube,
    xing: row.lead_xing,
  };
  for (const [key, val] of Object.entries(fallbacks)) {
    if (val && !socials[key]) socials[key] = val;
  }
  return socials;
}

function buildSocialHref(platform, value) {
  if (!value || !value.trim()) return null;
  const v = value.trim();
  if (v.startsWith('http')) return v;
  switch (platform) {
    case 'linkedin': return `https://linkedin.com/in/${v}`;
    case 'facebook': return `https://facebook.com/${v}`;
    case 'instagram': return `https://instagram.com/${v.replace('@', '')}`;
    case 'x': return `https://x.com/${v.replace('@', '')}`;
    case 'tiktok': return `https://tiktok.com/@${v.replace('@', '')}`;
    case 'youtube': return `https://youtube.com/@${v.replace('@', '')}`;
    case 'xing': return `https://xing.com/profile/${v}`;
    case 'website': return `https://${v}`;
    default: return `https://${v}`;
  }
}

function renderStatus(rowData, rowId, editedData) {
  const contactStatus = editedData[`${rowId}-lead_contacted`] || rowData.lead_contacted || "Not Contacted";

  // "In Outreach" always takes priority — show the pipeline badge, not a tag
  if (contactStatus === 'In Outreach') {
    return <span className="contact-status-badge status-in-outreach">In Outreach</span>;
  }

  if (isAgentFailedStatus(contactStatus)) {
    return <span className="contact-status-badge status-outreach-failed">{contactStatus}</span>;
  }

  if (rowData.tags && Object.keys(rowData.tags).length > 0) {
    const lastTag = Object.values(rowData.tags)[Object.values(rowData.tags).length - 1];
    if (lastTag && lastTag.tag) {
      const statusClass = lastTag.tag.toLowerCase().replace(/\s+/g, '-');
      return (
        <span className={`contact-status-badge status-${statusClass}`} style={{ backgroundColor: lastTag.color, color: '#fff' }}>
          {lastTag.tag}
          {lastTag.icon && <img src={lastTag.icon} alt="" className="status-tag-icon" style={{ marginLeft: '4px', width: '14px', height: '14px' }}/>}
        </span>
      );
    }
  }
  const statusClass = contactStatus.toLowerCase().replace(/\s+/g, '-');

  let daysSinceLastEmail = "";
  if (rowData.lead_last_email && rowData.lead_last_email.timestamp) {
    const diffDays = Math.floor((Date.now() - parseInt(rowData.lead_last_email.timestamp)) / 86400000);
    daysSinceLastEmail = `| ${diffDays} ${diffDays === 1 ? 'Day' : 'Days'}`;
  }

  let shortStatus = contactStatus;
  if (contactStatus.includes("Follow-up") || contactStatus.includes("Follow-Up")) {
    shortStatus = contactStatus.replace(/First|Second|Third/, (m) => ({First: '1.', Second: '2.', Third: '3.'}[m])).replace("Follow-Up", "Follow-up");
  }

  return <span className={`contact-status-badge status-${statusClass}`}>{shortStatus} {daysSinceLastEmail && <span className="days-since">{daysSinceLastEmail}</span>}</span>;
}

const WorldDbTableRow = memo(({
  row, rowId, index, orderedColumns, isSelectMode, selectedRows, editedData,
  warmupFlows, completingFlowStep, emailTemplates, userId,
  customFieldTypes,
  spyLoadingRows, analyzerLoadingRows, potentialLoadingRows,
  onToggleRowSelection, onEdit, onEmailAiClick, onSpyClick, onWhatsAppClick,
  onOpenFlowSelect, onOpenDecisionMakers, onOpenSocialEdit, onOpenNotes, onOpenLeadStatus, onOpenIntel, onOpenIgResearch, onOpenSeoResearch, onFindSimilar,
  onOpenTemplateModal, onToggleFavorite,
  duplicateAlert, onOpenDuplicateAction,
  statusPanelOpen,
}) => {
  const hasReplied = row.lead_reply === true;
  const isRowDisqualified = row.isDisqualified === true;
  // "In Outreach" dims the row and disables the mail button so nobody interferes
  // while the job drives the contact. A waiting prepared mail inverts that: the
  // job deliberately stopped and now needs the user to send it.
  const isInOutreach = row.lead_contacted === 'In Outreach' && row.has_prepared_email !== true;
  const isRowFavorite = !!(row.tags && row.tags.favorite);

  const potentialScoreRaw = row.potential_score;
  const potentialScore = (typeof potentialScoreRaw === 'number' && !Number.isNaN(potentialScoreRaw))
    ? potentialScoreRaw
    : (potentialScoreRaw != null && potentialScoreRaw !== '' && !Number.isNaN(parseInt(potentialScoreRaw, 10)) ? parseInt(potentialScoreRaw, 10) : null);
  const hasPotentialScore = potentialScore !== null && potentialScore >= 1;
  const isPotentialLoading = !!(potentialLoadingRows && potentialLoadingRows[rowId]);
  // "No info found" is its own state: the AI couldn't verify anything, so the low
  // score is a priced-in disadvantage — not a neutral middle. Shown with a
  // distinct icon so it never reads like a normal weak-fit score.
  const isPotentialNoInfo = hasPotentialScore && row.potential_analysis?.dataConfidence === 'none';
  const potentialTier = hasPotentialScore
    ? (isPotentialNoInfo ? 'nodata' : (potentialScore >= 6 ? 'high' : potentialScore >= 3 ? 'mid' : 'low'))
    : null;
  const potentialReasons = (hasPotentialScore && Array.isArray(row.potential_analysis?.bullets))
    ? row.potential_analysis.bullets.filter(b => typeof b === 'string' && b.trim()).slice(0, 2)
    : [];
  const hasStrikethroughTag = !!(row.tags && (
    STRIKETHROUGH_TAGS.some(tagId => row.tags[tagId]) ||
    Object.keys(row.tags).some(key =>
      STRIKETHROUGH_TAGS.includes(key.toLowerCase().replace(/\s+/g, '_'))
    )
  ));

  const [showWaPopup, setShowWaPopup] = useState(false);
  const [potentialTip, setPotentialTip] = useState(null);

  // Styled hover box for the potential score. Positioned with fixed coords so it
  // is never clipped by the scrollable table body and expands to the right.
  const handleScoreEnter = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setPotentialTip({ top: rect.top + rect.height / 2, left: rect.right + 10 });
  };
  const handleScoreLeave = () => setPotentialTip(null);

  return (
    <tr
      className={`worldDbTableRow ${hasReplied ? 'has-replied' : ''} ${isRowDisqualified ? 'disqualified' : ''} ${hasStrikethroughTag ? 'strikethrough-tag' : ''} ${isRowFavorite ? 'is-favorite' : ''} ${isInOutreach ? 'in-outreach' : ''} ${duplicateAlert ? 'has-duplicate' : ''} ${statusPanelOpen ? 'status-panel-open' : ''}`}
      style={index >= 25 ? { animationDuration: '0ms' } : undefined}
    >
      {orderedColumns.map((colKey) => {
        if (colKey === "selectCheckbox") {
          return isSelectMode ? (
            <td key={colKey} className="worldDbTableCell selectCheckbox">
              <div className={`bc-checkbox ${selectedRows[rowId] ? 'checked' : ''}`} onMouseDown={(e) => { if (e.shiftKey) e.preventDefault(); }} onClick={(e) => { e.stopPropagation(); onToggleRowSelection(rowId, e.nativeEvent.shiftKey); }}>
                <svg viewBox="0 0 12 10" fill="none"><path d="M1 5.5L4 8.5L11 1.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </div>
            </td>
          ) : null;
        }
        if (colKey === "rowNumber") {
          // The score badge is shown ALONGSIDE the number/heart marker so the
          // analysis result stays visible even on favorites (which keep their heart).
          let scoreBadge = null;
          if (isPotentialLoading) {
            scoreBadge = <span className="potentialScoreBadge potentialScoreBadge--loading" title="Potential-Analyse läuft…"><i className="fa fa-spinner fa-spin"></i></span>;
          } else if (isPotentialNoInfo) {
            scoreBadge = (
              <span
                className="potentialScoreBadge potentialScoreBadge--nodata"
                onMouseEnter={handleScoreEnter}
                onMouseLeave={handleScoreLeave}
                title="Keine Infos gefunden — als Nachteil gewertet"
              >
                <i className="fa fa-question potentialScoreNoData"></i>
              </span>
            );
          } else if (hasPotentialScore) {
            scoreBadge = (
              <span
                className={`potentialScoreBadge potentialScoreBadge--${potentialTier}`}
                onMouseEnter={handleScoreEnter}
                onMouseLeave={handleScoreLeave}
              >
                <span className="potentialScoreNum">{potentialScore}</span>
                <span className="potentialScoreDenom">/10</span>
              </span>
            );
          }
          // Duplikat-Konflikt hat Vorrang vor Favorit/Nummer: der User soll
          // hier sofort erkennen, dass dieser Kontakt eine offene Kollision
          // hat und mit einem Klick zum Aktions-Modal springen können.
          const markerContent = duplicateAlert
            ? (
              <button
                type="button"
                className="rowNumber-dupBtn"
                title="Duplicate conflict — show options"
                onClick={(e) => { e.stopPropagation(); onOpenDuplicateAction?.(rowId); }}
              >
                <i className="fa fa-exclamation" aria-hidden />
              </button>
            )
            : isRowFavorite
              ? <i className="fa fa-heart rowNumber-heart"></i>
              : index + 1;
          return (
            <td key={colKey} className={`worldDbTableCell rowNumber ${scoreBadge ? 'rowNumber--withScore' : ''}`} onClick={(e) => {
              if (duplicateAlert) return; // Click öffnet Alert-Modal via Button oben
              e.stopPropagation();
              onToggleFavorite?.(rowId);
            }}>
              <div className="rowNumber-inner">
                {scoreBadge}
                <span className="rowNumber-marker">
                  <span className="rowNumber-default">{markerContent}</span>
                  <span className="rowNumber-hover">
                    <i className={`${isRowFavorite ? 'fa' : 'far'} fa-heart rowNumber-heart-toggle ${isRowFavorite ? 'rowNumber-heart-toggle--active' : ''}`}></i>
                  </span>
                </span>
              </div>
              {potentialTip && hasPotentialScore && createPortal(
                <div
                  className={`potentialTip potentialTip--${potentialTier}`}
                  style={{ top: potentialTip.top, left: potentialTip.left }}
                >
                  <div className="potentialTip-head">
                    <span className={`potentialTip-score potentialTip-score--${potentialTier}`}>
                      {isPotentialNoInfo ? <><i className="fa fa-question" /> {potentialScore}/10</> : `${potentialScore}/10`}
                    </span>
                    {(isPotentialNoInfo || row.potential_analysis?.fitLabel) && (
                      <span className="potentialTip-label">{isPotentialNoInfo ? 'Keine Infos gefunden' : row.potential_analysis.fitLabel}</span>
                    )}
                  </div>
                  {isPotentialNoInfo && (
                    <p className="potentialTip-nodataNote">
                      <i className="fa fa-circle-info" /> Kaum verwertbare Infos im Web gefunden — das wurde als Nachteil eingerechnet, nicht als neutral.
                    </p>
                  )}
                  {row.potential_analysis?.summary && (
                    <p className="potentialTip-summary">{row.potential_analysis.summary}</p>
                  )}
                  {potentialReasons.length > 0 && (
                    <div className="potentialTip-reasons">
                      <span className="potentialTip-reasons-title">Hauptgründe</span>
                      <ul>
                        {potentialReasons.map((b, i) => (
                          <li key={i}><i className="fa fa-circle-check" />{b}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>,
                document.body
              )}
            </td>
          );
        }
        if (colKey === "warmupFlow") {
          return <td key={colKey} className={`worldDbTableCell warmupFlowCell ${getFlowStepTheme(row, warmupFlows)}`}><WarmupFlowBadge rowData={row} rowId={rowId} warmupFlows={warmupFlows} completingFlowStep={completingFlowStep} onOpenFlowSelect={onOpenFlowSelect} /></td>;
        }
        if (colKey === "contacted") {
          return (
            <td key={colKey} className="worldDbTableCell contactedCell">
              <button
                type="button"
                className={`lead-status-trigger ${statusPanelOpen ? 'is-open' : ''}`}
                title="Open lead status"
                aria-label="Open lead status"
                onClick={(e) => { e.stopPropagation(); if (onOpenLeadStatus) onOpenLeadStatus(row); }}
              >
                {renderStatus(row, rowId, editedData)}
              </button>
            </td>
          );
        }
        if (colKey === "aiMail") {
          const hasEmail = getAllContactEmails(row).some(e => e.email && e.email.trim() !== "" && !e.emailMissing);
          const activeFlow = row.warmupFlow;
          let isFlowBlockingEmail = false;
          let isFlowEmailReady = false;
          const EMAIL_STEP_TYPES = EMAIL_ACTION_TYPES;
          if (activeFlow && activeFlow.flowId) {
            const matchingFlow = warmupFlows.find(f => f.id === activeFlow.flowId);
            if (matchingFlow?.steps) {
              let currentStep = matchingFlow.steps[activeFlow.currentStepIndex];
              const now = Date.now();
              const isStepReady = now >= (activeFlow.nextStepAvailableAt || 0);
              if (currentStep?.nodeType === 'delay' && isStepReady && matchingFlow.visualData?.edges) {
                const outgoing = matchingFlow.visualData.edges.filter(e => e.source === currentStep.id);
                if (outgoing.length > 0) {
                  const next = matchingFlow.steps.find(s => s.id === outgoing[0].target);
                  if (next) currentStep = next;
                }
              }
              const isEmailStep = EMAIL_STEP_TYPES.includes(currentStep?.type);
              isFlowBlockingEmail = !isEmailStep;
              isFlowEmailReady = isEmailStep && isStepReady;
            }
          }
          const isDisabled = !hasEmail || isRowDisqualified || isFlowBlockingEmail || isInOutreach;
          const disabledTitle = isInOutreach ? "Contact is in an active outreach job" : isRowDisqualified ? "Contact is disqualified" : !hasEmail ? "No valid email" : isFlowBlockingEmail ? "Current flow step is not Send Email" : hasReplied ? "Lead replied" : "Create AI Mail";
          return (
            <td key={colKey} className="worldDbTableCell aiMail">
              <button className={`ai-mail-btn ${isDisabled ? 'disabled' : ''} ${hasReplied ? 'replied' : ''} ${isFlowEmailReady ? 'flow-email-ready' : ''}`} onClick={() => !isDisabled && onEmailAiClick(index)} disabled={isDisabled} title={disabledTitle}>
                {hasReplied ? (<><span className="replied-icon">✓</span><p style={{fontSize: '9px'}}>REPLIED</p></>) : (<><div className="ai-chat-icon-container"><img src="/img/mailerle.webp" alt="Chat" className="ai-chat-icon" /></div> Mail <span className="ai-mail-tag">AI</span></>)}
              </button>
            </td>
          );
        }
        if (colKey === "spy") {
          const isLoading = spyLoadingRows[row.id] === true || analyzerLoadingRows?.[row.id] === true;

          return (
            <td key={colKey} className="worldDbTableCell spyCell">
              <div
                role="button"
                tabIndex={0}
                className={`research-trigger-btn ${isLoading ? 'research-loading' : ''}`}
                onClick={() => !isLoading && onSpyClick(index)}
                onKeyDown={(e) => e.key === 'Enter' && !isLoading && onSpyClick(index)}
                title={isLoading ? 'Analyzing...' : 'Research this contact'}
              >
                <div className="research-trigger-icon">
                  {isLoading ? (
                    <div className="research-trigger-spinner"></div>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M12 2L2 7l10 5 10-5-10-5z" fill="currentColor" opacity="0.3"/><path d="M2 17l10 5 10-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M2 12l10 5 10-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  )}
                </div>
              </div>
            </td>
          );
        }
        if (colKey === "whatsapp") {
          const hasWhatsappSent = row.hasWhatsappButtonPressed || false;
          return (
            <td key={colKey} className="worldDbTableCell whatsapp">
              <button className={`whatsapp-btn ${hasWhatsappSent ? 'whatsapp-sent' : ''} ${isRowDisqualified ? 'disabled' : ''}`} onClick={() => !isRowDisqualified && onWhatsAppClick(index)} disabled={isRowDisqualified} title={isRowDisqualified ? "Contact is disqualified" : ""}>
                <div className="whatsapp-icon-container"><img src="/img/whats.svg" alt="WhatsApp" className="whatsapp-icon" /></div>{hasWhatsappSent ? 'SENT' : 'WHATS'}
              </button>
            </td>
          );
        }
        if (colKey === "lead_reply") {
          const replied = row ? (editedData[`${rowId}-${colKey}`] ?? row[colKey] ?? false) : false;
          return <td key={colKey} className={`worldDbTableCell ${colKey}`}>{replied ? <span className="replied-badge"><span className="replied-icon">✓</span> Replied</span> : <span className="not-replied-badge">Pending</span>}</td>;
        }
        if (colKey === "timestamp") {
          const ts = row.timestamp;
          let daysAgo = "";
          if (ts) { const d = Math.floor((new Date() - new Date(ts)) / 86400000); daysAgo = d + " day" + (d === 1 ? "" : "s"); }
          return <td key={colKey} className={`worldDbTableCell ${colKey}`}>{daysAgo}</td>;
        }
        if (colKey === "lastEdited") {
          const lastEdit = (row.lead_last_edit && row.lead_last_edit.time) || "";
          let hoursAgo = "";
          if (lastEdit) { const h = Math.floor((new Date() - new Date(lastEdit)) / 3600000); hoursAgo = h + " h"; }
          return <td key={colKey} className={`worldDbTableCell ${colKey}`}>{hoursAgo}</td>;
        }
        if (colKey === "lead_website") {
          const websiteUrl = editedData[`${rowId}-${colKey}`] ?? row[colKey] ?? "";
          const wa = row.websiteAnalysis;
          const openWebsite = (e) => {
            e.stopPropagation();
            let url = websiteUrl.trim();
            if (url && !url.startsWith('http')) url = 'https://' + url;
            if (url) window.open(url, '_blank', 'noopener,noreferrer');
          };
          const ageObj = wa?.websiteAge || wa?.website_age;
          const ageLabel = ageObj?.label;
          const ageScore = ageObj?.score;
          const ageEra = ageObj?.estimatedEra || ageObj?.estimated_era || '';
          return (
            <td key={colKey} className={`worldDbTableCell ${colKey} website-cell`}>
              <div className="website-container">
                <div className="website-content" contentEditable suppressContentEditableWarning onBlur={(e) => onEdit(rowId, colKey, e.target.textContent)}>{websiteUrl}</div>
                {websiteUrl && (
                  <button className="external-link-btn" onClick={openWebsite} title="Website in neuem Tab öffnen">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg>
                  </button>
                )}
                {wa && (
                  <button
                    className={`wa-info-toggle ${showWaPopup ? 'active' : ''}`}
                    onClick={(e) => { e.stopPropagation(); setShowWaPopup(v => !v); }}
                    title="Website Analysis"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                  </button>
                )}
              </div>
              {showWaPopup && wa && (
                <>
                  <div className="wa-popup-backdrop" onClick={() => setShowWaPopup(false)} />
                  <div className="wa-popup-card">
                    <div className="wa-popup-head">
                      <span>Website Analysis</span>
                      <button className="wa-popup-close" onClick={() => setShowWaPopup(false)}>&times;</button>
                    </div>
                    <div className="wa-popup-body">
                      {wa.cms && wa.cms !== 'Unknown' && (
                        <div className="wa-popup-row">
                          <span className="wa-popup-lbl">CMS</span>
                          <span className="wa-popup-val">{wa.cms} <small>{Math.round((wa.cms_confidence ?? wa.cmsConfidence ?? 0) * 100)}%</small></span>
                        </div>
                      )}
                      {(wa.is_shop || wa.is_shopware) && (
                        <div className="wa-popup-row">
                          <span className="wa-popup-lbl">{wa.is_shopware ? 'Shopware' : 'Shop'}</span>
                          <span className="wa-popup-val">{wa.is_shopware && wa.version_estimate && wa.version_estimate !== 'Unknown' ? wa.version_estimate : 'Yes'}</span>
                        </div>
                      )}
                      {(wa.product_count != null || wa.productCount != null) && (() => {
                        const pc = wa.product_count ?? wa.productCount;
                        return (
                          <div className="wa-popup-row">
                            <span className="wa-popup-lbl">Products</span>
                            <span className="wa-popup-val">{pc >= 1000 ? `${(pc / 1000).toFixed(1).replace(/\.0$/, '')}k` : pc}</span>
                          </div>
                        );
                      })()}
                      {ageLabel && (
                        <div className="wa-popup-row">
                          <span className="wa-popup-lbl">Website Age</span>
                          <span className="wa-popup-val" style={{ color: ageScore >= 70 ? '#22c55e' : ageScore >= 40 ? '#f59e0b' : '#ef4444' }}>
                            {ageLabel}{ageEra && <small> ({ageEra})</small>}
                          </span>
                        </div>
                      )}
                      {wa.tech_stack_maturity && wa.tech_stack_maturity !== 'standard' && (
                        <div className="wa-popup-row">
                          <span className="wa-popup-lbl">Tech</span>
                          <span className="wa-popup-val">{wa.tech_stack_maturity}</span>
                        </div>
                      )}
                      {wa.industry && (
                        <div className="wa-popup-row">
                          <span className="wa-popup-lbl">Industry</span>
                          <span className="wa-popup-val">{wa.industry}</span>
                        </div>
                      )}
                      {wa.recommended_sales_angle && wa.recommended_sales_angle !== 'Low Priority' && (
                        <div className="wa-popup-row">
                          <span className="wa-popup-lbl">Sales Angle</span>
                          <span className="wa-popup-val">{wa.recommended_sales_angle}</span>
                        </div>
                      )}
                      {wa.company_name && (
                        <div className="wa-popup-row">
                          <span className="wa-popup-lbl">Company</span>
                          <span className="wa-popup-val">{wa.company_name}</span>
                        </div>
                      )}
                      {wa.city && (
                        <div className="wa-popup-row">
                          <span className="wa-popup-lbl">City</span>
                          <span className="wa-popup-val">{wa.city}</span>
                        </div>
                      )}
                      {wa.analyzedAt && (
                        <div className="wa-popup-ts">Scanned {new Date(wa.analyzedAt).toLocaleDateString('de-DE')}</div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </td>
          );
        }
        if (colKey === "lead_socials") {
          const socials = parseSocials(row);
          const entries = Object.entries(socials).filter(([, v]) => v && v.trim());
          return (
            <td key={colKey} className="worldDbTableCell socials-cell">
              <div className="socials-icons">
                {entries.map(([platform, url]) => {
                  const p = socialPlatforms.find(sp => sp.key === platform);
                  if (!p) return null;
                  return (
                    <a key={platform} href={buildSocialHref(platform, url)} target="_blank" rel="noopener noreferrer" className="social-icon-link" style={{ color: p.color }} title={p.label} onClick={e => e.stopPropagation()}>
                      <i className={p.icon}></i>
                    </a>
                  );
                })}
                <button className="social-add-btn" title="Social Media bearbeiten" onClick={(e) => { e.stopPropagation(); onOpenSocialEdit(rowId, row); }}>
                  <i className="fa fa-plus"></i>
                </button>
              </div>
            </td>
          );
        }
        if (colKey === "lead_decision_makers") {
          const raw = row.lead_decision_makers;
          let dms = [];
          try {
            if (raw && typeof raw === 'string') dms = JSON.parse(raw);
            else if (Array.isArray(raw)) dms = raw;
          } catch (e) {}
          const has = dms && dms.length > 0;
          return (
            <td key={colKey} className={`worldDbTableCell decision-makers-cell${row._sparkle ? ' dm-sparkle' : ''}`}>
              <div className="dm-icons">
                {has && dms.slice(0, 4).map((dm, i) => (
                  <button key={i} className={`dm-icon-avatar ${dm.email ? 'has-email' : ''} ${dm.contacted ? 'dm-contacted' : ''}`} title={dm.name || 'Decision Maker'} onClick={(e) => { e.stopPropagation(); onOpenDecisionMakers({ isOpen: true, contactData: row, decisionMakers: dms }); }}>
                    {dm.contacted ? <i className="fa fa-check"></i> : (dm.name || '?').charAt(0).toUpperCase()}
                  </button>
                ))}
                {has && dms.length > 4 && <span className="dm-icon-more" title={`${dms.length} Decision Makers`}>+{dms.length - 4}</span>}
                <button className="social-add-btn" title="Decision Makers bearbeiten" onClick={(e) => { e.stopPropagation(); onOpenDecisionMakers({ isOpen: true, contactData: row, decisionMakers: dms }); }}>
                  <i className="fa fa-user-plus"></i>
                </button>
              </div>
            </td>
          );
        }
        if (colKey === "lead_score") {
          const score = row.lead_score ? parseInt(row.lead_score) : null;
          const hasScore = score !== null && !isNaN(score);
          const tier = hasScore ? getLeadScoreTier(score) : null;
          return (
            <td key={colKey} className="worldDbTableCell lead-score-cell">
              {tier ? (
                <div className={`lead-score-badge ${tier.className}`} title={`Lead Score: ${score}/100`}>
                  <span className="lead-score-number">{score}</span>
                  <span className="lead-score-label">{tier.label}</span>
                </div>
              ) : <span className="lead-score-empty">-</span>}
            </td>
          );
        }
        if (colKey === "websiteAnalysis") {
          const wa = row.websiteAnalysis;
          if (!wa) {
            return (
              <td key={colKey} className="worldDbTableCell wa-cell">
                <span className="wa-empty">-</span>
              </td>
            );
          }
          const ageObj = wa.websiteAge || wa.website_age;
          const ageLabel = ageObj?.label;
          const ageScore = ageObj?.score;
          const ageEra = ageObj?.estimatedEra || ageObj?.estimated_era || '';
          const ageColor = ageScore >= 70 ? '#22c55e' : ageScore >= 40 ? '#f59e0b' : '#ef4444';
          return (
            <td key={colKey} className="worldDbTableCell wa-cell">
              <div className="wa-compact">
                {wa.cms && (
                  <span className="wa-cms-badge" title={`Confidence: ${Math.round((wa.cmsConfidence || wa.cms_confidence || 0) * 100)}%`}>
                    {wa.cms}
                  </span>
                )}
                {(wa.product_count != null || wa.productCount != null) && (() => {
                  const pc = wa.product_count ?? wa.productCount;
                  return (
                    <span className="wa-chip" title="Estimated products">
                      <i className="fa fa-box" />
                      {pc >= 1000 ? `${(pc / 1000).toFixed(1).replace(/\.0$/, '')}k` : pc}
                    </span>
                  );
                })()}
                {ageScore != null && (
                  <span className="wa-age-pill" style={{ borderColor: ageColor, color: ageColor }} title={`Website Age Score: ${ageScore}/100 — ${ageEra}`}>
                    {ageLabel || `${ageScore}`}
                  </span>
                )}
                {wa.industry && (
                  <span className="wa-chip wa-chip-industry" title="Industry">{wa.industry}</span>
                )}
              </div>
            </td>
          );
        }
        if (colKey === "intelligence") {
          if (userId && row.id && !signalsAreFresh(row.leadSignals, row)) {
            enqueueForClassification(userId, row.id, row);
          }
          const signals = row.leadSignals || {};
          const u = (v) => v && v !== 'unknown';
          const hasSignals = u(signals.industry) || u(signals.sizeProxy) || u(signals.b2b_b2c) || u(signals.offerType) || (signals.tags && signals.tags.length > 0);
          const enthusiasm = row.lead_happyness ? parseInt(row.lead_happyness) : null;
          const spyConf = row.spy_confidence_score ? parseInt(row.spy_confidence_score) : null;
          const signalCount = [signals.industry, signals.sizeProxy, signals.b2b_b2c, signals.offerType, ...(signals.tags || [])].filter(v => v && v !== 'unknown').length + (enthusiasm > 0 ? 1 : 0) + (spyConf > 0 ? 1 : 0);

          return (
            <td key={colKey} className="worldDbTableCell intel-cell">
              <div className={`intel-bar ${hasSignals ? 'intel-bar-active' : ''}`} onClick={(e) => { e.stopPropagation(); onOpenIntel(row); }}>
                {hasSignals ? <div className="intel-bar-check">✓</div> : <div className="intel-bar-dot" />}
                {hasSignals ? (
                  <>
                    {signals.industry && signals.industry !== 'unknown' && <span className="intel-bar-tag">{signals.industry}</span>}
                    {signals.b2b_b2c && signals.b2b_b2c !== 'unknown' && <span className="intel-bar-badge">{signals.b2b_b2c.toUpperCase()}</span>}
                    {signals.sizeProxy && signals.sizeProxy !== 'unknown' && <span className="intel-bar-badge intel-bar-size">{signals.sizeProxy}</span>}
                    {enthusiasm > 0 && <span className={`intel-bar-meter ${enthusiasm >= 7 ? 'hot' : enthusiasm >= 4 ? 'warm' : 'cold'}`}>{enthusiasm}</span>}
                    <span className="intel-bar-count">{signalCount}</span>
                  </>
                ) : signals.signalsVersion ? (
                  <span className="intel-bar-nodata">Zu wenig Daten</span>
                ) : (
                  <>
                    <span className="intel-bar-ghost" style={{width: 48}} />
                    <span className="intel-bar-ghost" style={{width: 28}} />
                    <span className="intel-bar-ghost" style={{width: 20}} />
                  </>
                )}
              </div>
            </td>
          );
        }
        if (colKey === "lead_pacing") {
          const pacing = row.lead_pacing;
          const hasPacing = pacing && ['formal', 'neutral', 'casual'].includes(pacing);
          const pacingLabels = { formal: 'Formal', neutral: 'Neutral', casual: 'Casual' };
          return (
            <td key={colKey} className="worldDbTableCell lead-pacing-cell">
              {hasPacing ? <span className={`lead-pacing-badge ${pacing}`}>{pacingLabels[pacing]}</span> : <span className="lead-pacing-empty">-</span>}
            </td>
          );
        }
        if (colKey === "lead_template") {
          const introTpl = row.lead_template || "";
          const stageMap = row.lead_templates || {};
          const stageCount = Object.entries(stageMap).filter(([k, v]) => k !== 'intro' && v).length;
          const introName = introTpl ? (emailTemplates.find(t => t.id === introTpl)?.topic || 'Custom') : '';
          return (
            <td key={colKey} className="worldDbTableCell lead-template-cell">
              <button
                className={`lead-template-badge ${introTpl ? 'has-template' : ''}`}
                onClick={() => onOpenTemplateModal && onOpenTemplateModal(rowId, row)}
                title="Configure templates per stage"
              >
                <span className="lead-template-badge-label">{introTpl ? introName : 'Default'}</span>
                {stageCount > 0 && <span className="lead-template-badge-count">+{stageCount}</span>}
              </button>
            </td>
          );
        }
        if (colKey === "business_name") {
          const nameValue = editedData[`${rowId}-business_name`] ?? row.business_name ?? "";
          const notesCount = row.lead_notes?.length || 0;
          const openTaskUids = Array.isArray(row.open_task_assignees) ? row.open_task_assignees : [];
          const hasSignals = !!row.leadSignals;
          const hasIgInsight = !!row.researchIntelligence?.instagramInsight;
          const hasSeoAnalysis = !!row.researchIntelligence?.seoAnalysis?.data;
          const seoScannedAt = row.researchIntelligence?.seoAnalysis?.scannedAt;

          let dmSearched = 0, dmFound = 0;
          try {
            const dms = typeof row.lead_decision_makers === 'string' ? JSON.parse(row.lead_decision_makers) : row.lead_decision_makers;
            if (Array.isArray(dms)) {
              for (const dm of dms) {
                if (dm.emailSearched) { dmSearched++; if (dm.email && !dm.emailNotFound) dmFound++; }
              }
            }
          } catch {}
          // The "R" (Research) badge also covers the Potential Analyser: its
          // Smart-Combo engine does real Google research, so an analysed contact
          // is genuinely "researched". We surface R without touching
          // spy_last_updated (which gates other research-enrichment logic).
          const researchedAt = row.spy_last_updated || row.potential_analysis?.analyzedAt || null;
          const hasBadges = row.hasCleaned || researchedAt || dmSearched > 0 || row.website_analysis_at || hasSeoAnalysis;

          return (
            <td key={colKey} className="worldDbTableCell business_name">
              <div className="business-name-wrap">
                <span contentEditable suppressContentEditableWarning onBlur={(e) => onEdit(rowId, "business_name", e.target.textContent)}>{nameValue}</span>
                {hasIgInsight && (
                  <button className="bname-action-btn bname-ig-btn" title="Instagram Posts" onClick={(e) => { e.stopPropagation(); onOpenIgResearch(row); }}>
                    <i className="fab fa-instagram" />
                  </button>
                )}
                {hasSeoAnalysis && (
                  <button className="bname-action-btn bname-seo-btn" title="SEO Analysis" onClick={(e) => { e.stopPropagation(); onOpenSeoResearch && onOpenSeoResearch(row); }}>
                    <i className="fa fa-magnifying-glass-chart" />
                  </button>
                )}
                {hasSignals && (
                  <button className="bname-action-btn bname-similar-btn" title="Find similar contacts" onClick={(e) => { e.stopPropagation(); onFindSimilar(row); }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><circle cx="9" cy="9" r="7" stroke="currentColor" strokeWidth="2"/><circle cx="15" cy="15" r="7" stroke="currentColor" strokeWidth="2"/></svg>
                  </button>
                )}
                <button className={`bname-action-btn bname-notes-btn ${notesCount > 0 ? 'has-notes' : 'no-notes'}`} title={notesCount > 0 ? `${notesCount} Note${notesCount !== 1 ? 's' : ''}` : 'Add note'} onClick={(e) => { e.stopPropagation(); e.preventDefault(); onOpenNotes(row); }}>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  {notesCount > 0 && <span>{notesCount}</span>}
                </button>
                {openTaskUids.length > 0 && (
                  <button
                    className={`bname-action-btn bname-task-bell ${openTaskUids.includes(auth.currentUser?.uid) ? 'is-mine' : ''}`}
                    title={openTaskUids.includes(auth.currentUser?.uid) ? 'Open task for you' : 'Open task for your team'}
                    onClick={(e) => { e.stopPropagation(); e.preventDefault(); onOpenNotes(row); }}
                  >
                    <i className="fa fa-bell" />
                  </button>
                )}
                {hasBadges && (
                  <div className="bname-badge-stack">
                    {row.hasCleaned && <span className="bname-badge bname-badge-c" title={`AI Cleaner – Contact was cleaned & verified${row.cleanedAt ? ` on ${new Date(row.cleanedAt).toLocaleDateString('de-DE')}` : ''}`}>C</span>}
                    {researchedAt && <span className="bname-badge bname-badge-r" title={row.spy_last_updated ? `Web Research – Business info enriched via AI Spy on ${new Date(row.spy_last_updated).toLocaleDateString('de-DE')}` : `Research – Analysed via Potential Analyser on ${new Date(researchedAt).toLocaleDateString('de-DE')}`}>R</span>}
                    {dmSearched > 0 && <span className="bname-badge bname-badge-d" title={`DM Finder – Email search done for ${dmSearched} decision maker${dmSearched !== 1 ? 's' : ''}, ${dmFound} found`}>D</span>}
                    {row.website_analysis_at && <span className="bname-badge bname-badge-w" title={`Website Analyzer – Scanned on ${new Date(row.website_analysis_at).toLocaleDateString('de-DE')}`}>W</span>}
                    {hasSeoAnalysis && <span className="bname-badge bname-badge-s" title={`SEO Analyzer – Scanned on ${seoScannedAt ? new Date(seoScannedAt).toLocaleDateString('de-DE') : 'unknown date'}`}>S</span>}
                  </div>
                )}
              </div>
            </td>
          );
        }
        if (colKey === "lead_instagram_followers") {
          const raw = editedData[`${rowId}-lead_instagram_followers`] ?? row.lead_instagram_followers;
          const num = parseInt(raw, 10) || 0;
          const display = num >= 1_000_000 ? (num / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M' : num >= 1_000 ? (num / 1_000).toFixed(1).replace(/\.0$/, '') + 'K' : num > 0 ? String(num) : '';
          return (
            <td key={colKey} className="worldDbTableCell lead_instagram_followers" title={num > 0 ? num.toLocaleString('de-DE') : ''} contentEditable suppressContentEditableWarning onBlur={(e) => {
              const parsed = parseInt(e.target.textContent.replace(/[^0-9]/g, ''), 10) || 0;
              onEdit(rowId, "lead_instagram_followers", parsed);
            }}>
              {display}
            </td>
          );
        }

        if (colKey === "lead_description") {
          const descValue = editedData[`${rowId}-lead_description`] ?? row.lead_description ?? "";
          const descText = (descValue !== null && typeof descValue === 'object') ? JSON.stringify(descValue) : String(descValue);
          return (
            <td key={colKey} className="worldDbTableCell lead_description">
              <div
                className="desc-expand-wrap"
                contentEditable
                suppressContentEditableWarning
                onFocus={(e) => {
                  const el = e.currentTarget;
                  el.classList.remove('desc-collapsing');
                  el.classList.add('desc-expanded');
                  requestAnimationFrame(() => {
                    el.style.maxHeight = Math.min(el.scrollHeight, 200) + 'px';
                  });
                }}
                onBlur={(e) => {
                  const el = e.currentTarget;
                  el.style.maxHeight = el.scrollHeight + 'px';
                  el.classList.remove('desc-expanded');
                  el.classList.add('desc-collapsing');
                  requestAnimationFrame(() => {
                    el.style.maxHeight = '';
                  });
                  setTimeout(() => el.classList.remove('desc-collapsing'), 130);
                  onEdit(rowId, "lead_description", el.textContent);
                }}
              >
                {descText}
              </div>
              <button
                type="button"
                className="desc-intel-btn"
                title="BonusClub Intelligence"
                aria-label="BonusClub Intelligence"
                onMouseDown={(e) => e.preventDefault()}
                onClick={(e) => { e.stopPropagation(); onOpenIntel(row); }}
              >
                <img className="desc-intel-mark" src="/img/star.svg" alt="" />
              </button>
            </td>
          );
        }

        if (colKey === "lead_email") {
          const rawEmail = editedData[`${rowId}-${colKey}`] ?? row.lead_email ?? "";
          const bc = row.emailBounceCheck;
          // Only flag when the recorded bad address is still the one shown here,
          // so a user who already cleaned up the email isn't misled.
          const stillBad = bc && bc.originalEmail &&
            String(rawEmail).toLowerCase().includes(String(bc.originalEmail).toLowerCase());
          const isSoftBounce = bc && bc.status === 'soft_bounce';
          const bounceLabel = stillBad
            ? (bc.action === 'reroute'
                ? `→ ${bc.reroutedTo || 'standard mailbox'}`
                : (bc.action === 'undeliverable'
                    ? `Undeliverable — ${isSoftBounce ? 'soft' : 'hard'} bounce`
                    : 'SafeGuard — not sent'))
            : null;
          const bounceTitle = stillBad
            ? (bc.action === 'reroute'
                ? `Bounce Reduction: ${bc.originalEmail} was invalid${bc.status ? ` (${bc.status})` : ''} — sent to ${bc.reroutedTo} instead`
                : (bc.action === 'undeliverable'
                    ? `${bc.originalEmail} ${isSoftBounce ? 'soft-bounced (temporary failure)' : 'hard-bounced'} — last send was undeliverable, this address is blocked from sending`
                    : `Bounce Reduction: ${bc.originalEmail} was invalid${bc.status ? ` (${bc.status})` : ''} — parked in SafeGuard, no email sent`))
            : undefined;
          return (
            <td
              key={colKey}
              className={`worldDbTableCell lead_email ${stillBad ? 'lead_email_bounce_invalid' : ''}`}
              contentEditable
              suppressContentEditableWarning
              title={bounceTitle}
              data-bounce-label={bounceLabel || undefined}
              onBlur={(e) => onEdit(rowId, colKey, e.target.textContent)}
            >
              {String(rawEmail)}
            </td>
          );
        }

        const rawValue = editedData[`${rowId}-${colKey}`] ?? row[colKey] ?? "";
        const isNumberField = customFieldTypes && customFieldTypes[colKey] === 'number';
        const cellValue = (rawValue !== null && typeof rawValue === 'object') ? JSON.stringify(rawValue) : String(rawValue);
        return (
          <td
            key={colKey}
            className={`worldDbTableCell ${colKey} ${isNumberField ? 'cf-number-cell' : ''}`}
            contentEditable
            suppressContentEditableWarning
            inputMode={isNumberField ? 'decimal' : undefined}
            onBlur={(e) => onEdit(rowId, colKey, e.target.textContent)}
          >
            {cellValue}
          </td>
        );
      })}
    </tr>
  );
});

WorldDbTableRow.displayName = 'WorldDbTableRow';

export { parseSocials };
export default WorldDbTableRow;
